import { useLingui } from "@lingui/react";
import { msg } from "@lingui/core/macro";
import { Link } from "@tanstack/react-router";
import {
  type DashboardCompaniesStats,
  type DashboardLocationsStats,
  type DashboardMaterialsStats,
  type DashboardMenusStats,
  type DashboardOrganizationsStats,
  type DashboardRecipesStats,
  type DashboardUsersStats,
} from "@/types/company";

import {
  Building,
  Building2,
  ChefHat,
  MapPin,
  Package,
  Users,
  UtensilsCrossed,
  type LucideIcon,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { statCardSurface } from "@/components/common/statCardSurface";

interface AdminStatsGridProps {
  isSuperAdmin: boolean;
  statsLoading: boolean;
  organizationsStats?: DashboardOrganizationsStats;
  companiesStats?: DashboardCompaniesStats;
  locationsStats?: DashboardLocationsStats;
  usersStats?: DashboardUsersStats;
  materialsStats?: DashboardMaterialsStats;
  recipesStats?: DashboardRecipesStats;
  menusStats?: DashboardMenusStats;
}

interface StatCardConfig {
  key: string;
  title: string;
  value: number;
  icon: LucideIcon;
  href: string;
  color: string;
  bgColor: string;
  show: boolean;
}

export function AdminStatsGrid({
  isSuperAdmin,
  statsLoading,
  organizationsStats,
  companiesStats,
  locationsStats,
  usersStats,
  materialsStats,
  recipesStats,
  menusStats,
}: AdminStatsGridProps) {
  const { i18n } = useLingui();

  const statCards: StatCardConfig[] = [
    {
      key: "organizations",
      title: i18n._(msg`Organizations`),
      value: organizationsStats?.totalOrganizations ?? 0,
      icon: Building,
      href: "/organizations",
      color: "text-(--brand-blue)",
      bgColor: "bg-(--brand-blue)/10",
      show: isSuperAdmin,
    },
    {
      key: "companies",
      title: i18n._(msg`Concepts`),
      value: companiesStats?.totalCompanies ?? 0,
      icon: Building2,
      href: "/companies",
      color: "text-(--brand-blue)",
      bgColor: "bg-(--brand-blue)/10",
      show: isSuperAdmin,
    },
    {
      key: "locations",
      title: i18n._(msg`Locations`),
      value: locationsStats?.totalLocations ?? 0,
      icon: MapPin,
      href: "/locations",
      color: "text-(--color-success-text)",
      bgColor: "bg-(--color-status-active)/10",
      show: true,
    },
    {
      key: "users",
      title: i18n._(msg`Users`),
      value: usersStats?.totalUsers ?? 0,
      icon: Users,
      href: "/users",
      color: "text-(--color-warning-text)",
      bgColor: "bg-(--color-warning)/10",
      show: true,
    },
    {
      key: "materials",
      title: i18n._(msg`Materials`),
      value: materialsStats?.totalMaterials ?? 0,
      icon: Package,
      href: "/materials",
      color: "text-(--color-accent-cyan)",
      bgColor: "bg-(--color-accent-cyan)/10",
      show: true,
    },
    {
      key: "recipes",
      title: i18n._(msg`Recipes`),
      value: recipesStats?.totalRecipes ?? 0,
      icon: ChefHat,
      href: "/recipes",
      color: "text-(--color-accent-pink)",
      bgColor: "bg-(--color-accent-pink)/10",
      show: true,
    },
    {
      key: "menus",
      title: i18n._(msg`Menus`),
      value: menusStats?.totalMenus ?? 0,
      icon: UtensilsCrossed,
      href: "/menus",
      color: "text-(--color-warning-text)",
      bgColor: "bg-(--color-warning)/10",
      show: true,
    },
  ].filter((card) => card.show);

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {statCards.map((stat) => {
        const Icon = stat.icon;
        return (
          <Link key={stat.key} to={stat.href}>
            <Card className={cn(statCardSurface, "hover:shadow-md transition-shadow cursor-pointer")}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
                <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                  <Icon className={`h-4 w-4 ${stat.color}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {statsLoading ? "..." : stat.value}
                </div>
                <p className="text-xs text-(--color-text-secondary)">{i18n._(msg`Click to manage`)}</p>
              </CardContent>
            </Card>
          </Link>
        );
      })}
    </div>
  );
}
